import { useState } from 'react';
import { Bell, Settings, User, LogOut } from 'lucide-react';
import { Button } from './ui/button';
// import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';

interface NavbarProps {
  currentView: string;
  selectedItem?: any;
  userInfo?: {
    name: string;
    email: string;
  };
  onSignOut?: () => void;
  onProfileClick?: () => void;
  isMobile?: boolean;
}

interface Notification {
  id: string;
  title: string;
  description: string;
  time: string;
  type: 'info' | 'warning' | 'success' | 'error';
  read: boolean;
}

const mockNotifications: Notification[] = [
  {
    id: '1',
    title: 'New Order Received',
    description: 'Order #12345 from Sarah Johnson - $1,299.00',
    time: '5 min ago',
    type: 'success',
    read: false,
  },
  {
    id: '2',
    title: 'Low Stock Alert',
    description: 'Modern Sectional Sofa has only 2 items left',
    time: '10 min ago',
    type: 'warning',
    read: false,
  },
  {
    id: '3',
    title: 'Discount Request',
    description: 'Customer requesting 20% off on dining table set',
    time: '15 min ago',
    type: 'info',
    read: false,
  },
  {
    id: '4',
    title: 'Showroom Visit Scheduled',
    description: 'Mike Chen scheduled visit for tomorrow 2 PM',
    time: '30 min ago',
    type: 'info',
    read: true,
  },
  {
    id: '5',
    title: 'Integration Connected',
    description: 'WhatsApp Business integration successfully connected',
    time: '1 hour ago',
    type: 'success',
    read: true,
  },
  {
    id: '6',
    title: 'User Permission Updated',
    description: 'Emily Davis permissions have been updated',
    time: '2 hours ago',
    type: 'info',
    read: true,
  },
];

const getPageTitle = (
  currentView: string,
  selectedItem?: any
): { title: string; subtitle?: string } => {
  switch (currentView) {
    case 'dashboard':
      return {
        title: 'Dashboard',
        subtitle: 'Overview of your store performance',
      };
    case 'products':
      return {
        title: 'Product Management',
        subtitle: 'Manage your store inventory',
      };
    case 'product-detail':
      return {
        title: selectedItem?.name || 'Product Details',
        subtitle: 'Product information and settings',
      };
    case 'product-edit':
      return {
        title: selectedItem ? 'Edit Product' : 'Add New Product',
        subtitle: selectedItem
          ? 'Update product information'
          : 'Create a new product',
      };
    case 'product-add':
      return {
        title: 'Add New Product',
        subtitle: 'Create a new product for your store',
      };
    case 'bulk-product-add':
      return {
        title: 'Bulk Product Import',
        subtitle: 'Import products in bulk using files or web scraping',
      };
    case 'users':
      return {
        title: 'User Management',
        subtitle: 'Manage user accounts and permissions',
      };
    case 'user-detail':
      return {
        title: selectedItem?.name || 'User Details',
        subtitle: 'User profile and activity',
      };
    case 'user-edit':
      return {
        title: selectedItem ? 'Edit User' : 'Add New User',
        subtitle: selectedItem
          ? 'Update user information'
          : 'Create a new user account',
      };
    case 'user-add':
      return { title: 'Add New User', subtitle: 'Create a new user account' };
    case 'branches':
      return {
        title: 'Branch Management',
        subtitle: 'Manage your store locations',
      };
    case 'branch-detail':
      return {
        title: selectedItem?.name || 'Branch Details',
        subtitle: 'Branch information and settings',
      };
    case 'branch-edit':
      return {
        title: selectedItem ? 'Edit Branch' : 'Add New Branch',
        subtitle: selectedItem
          ? 'Update branch information'
          : 'Create a new store location',
      };
    case 'branch-add':
      return {
        title: 'Add New Branch',
        subtitle: 'Create a new store location',
      };
    case 'chat':
      if (selectedItem) {
        return {
          title: `Chat with ${selectedItem.name}`,
          subtitle: 'Customer conversation',
        };
      }
      return {
        title: 'Chat Management',
        subtitle: 'Customer conversations and support',
      };
    case 'integration':
      return {
        title: 'Integration',
        subtitle: 'Connect external services and APIs',
      };
    case 'integration-config':
      return {
        title: `${selectedItem?.name || 'Integration'} Setup`,
        subtitle: 'Configure integration settings',
      };
    case 'profile':
      return {
        title: 'Profile & Settings',
        subtitle: 'Account settings and preferences',
      };
    default:
      return {
        title: 'Dashboard',
        subtitle: 'Overview of your store performance',
      };
  }
};

const getNotificationTypeColor = (type: Notification['type']) => {
  switch (type) {
    case 'success':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    case 'warning':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
    case 'error':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    case 'info':
    default:
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
  }
};

export function Header({
  currentView,
  selectedItem,
  userInfo = { name: 'Admin User', email: 'admin@homedecor.com' },
  onSignOut,
  onProfileClick,
  isMobile = false,
}: NavbarProps) {
  const [notifications, setNotifications] =
    useState<Notification[]>(mockNotifications);
  // const [searchQuery, setSearchQuery] = useState('');

  const unreadCount = notifications.filter((n) => !n.read).length;
  const pageInfo = getPageTitle(currentView, selectedItem);

  const markAsRead = (notificationId: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
    );
  };

  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  return (
    <header className="sticky top-0 z-20 flex h-14 items-center gap-4 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-4 lg:px-6">
      {/* Page Title Section */}
      <div className="flex-1">
        <div className={`${isMobile ? 'ml-12' : ''}`}>
          <h1 className="text-lg font-semibold">{pageInfo.title}</h1>
          {pageInfo.subtitle && !isMobile && (
            <p className="text-sm text-muted-foreground">{pageInfo.subtitle}</p>
          )}
        </div>
      </div>

      {/* Search Bar (Hidden on mobile) */}
      {/* {!isMobile && (
        <div className="relative w-64">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search..."
            className="pl-8"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      )} */}

      {/* Notifications */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" className="relative">
            <Bell className="h-4 w-4" />
            {unreadCount > 0 && (
              <Badge
                variant="destructive"
                className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80" align="end">
          <Card className="border-none shadow-none">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Notifications
              </CardTitle>
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={markAllAsRead}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Mark all as read
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-80">
                <div className="space-y-1">
                  {notifications.length === 0 ? (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      No notifications yet
                    </div>
                  ) : (
                    notifications.map((notification) => (
                      <div
                        key={notification.id}
                        className={`p-3 cursor-pointer hover:bg-muted/50 transition-colors ${
                          !notification.read ? 'bg-muted/30' : ''
                        }`}
                        onClick={() => markAsRead(notification.id)}
                      >
                        <div className="flex items-start space-x-3">
                          <div className="flex-1 space-y-1">
                            <div className="flex items-center space-x-2">
                              <p
                                className={`text-sm ${
                                  !notification.read ? 'font-medium' : ''
                                }`}
                              >
                                {notification.title}
                              </p>
                              <Badge
                                className={`text-xs ${getNotificationTypeColor(
                                  notification.type
                                )}`}
                              >
                                {notification.type}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground line-clamp-2">
                              {notification.description}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {notification.time}
                            </p>
                          </div>
                          {!notification.read && (
                            <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-1" />
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
              {notifications.length > 0 && (
                <>
                  <Separator />
                  <div className="p-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full text-xs"
                    >
                      View All Notifications
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </PopoverContent>
      </Popover>

      {/* User Menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="relative h-8 w-8 rounded-full">
            <Avatar className="h-8 w-8">
              <AvatarImage src="" alt={userInfo.name} />
              <AvatarFallback>{getInitials(userInfo.name)}</AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56" align="end" forceMount>
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium leading-none">
                {userInfo.name}
              </p>
              <p className="text-xs leading-none text-muted-foreground">
                {userInfo.email}
              </p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onProfileClick}>
            <User className="mr-2 h-4 w-4" />
            <span>Profile</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onProfileClick}>
            <Settings className="mr-2 h-4 w-4" />
            <span>Settings</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onSignOut}>
            <LogOut className="mr-2 h-4 w-4" />
            <span>Log out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
